import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiHelpers } from '@/lib/api';
import { toast } from 'sonner';
import { Puzzle, Check, Settings, Loader2, X } from 'lucide-react';
import { categoryIcons } from './constants';

interface PluginCardProps {
  plugin: any;
  projectId: string;
  isInstalled: boolean;
  showInstallButton?: boolean;
  onActionComplete?: () => void;
}

export function PluginCard({
  plugin,
  projectId,
  isInstalled,
  showInstallButton = true,
  onActionComplete,
}: PluginCardProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showConfigModal, setShowConfigModal] = useState(false);

  const Icon = categoryIcons[plugin.category] || Puzzle;

  /**
   * Resolve the *plugin manifest id* (e.g. "seo-copilot"), NOT the
   * pluginConfigs row's Mongo ObjectId.
   *
   * The marketplace endpoint returns plugins shaped as `{ id: 'seo-copilot' }`
   * (registry-derived; no `pluginId` field).
   *
   * The installed-plugins endpoint returns rows shaped as
   * `{ id: ObjectId('6a...'), pluginId: 'seo-copilot' }` — `id` here is
   * the pluginConfigs row's Mongo _id, NOT the manifest id.
   *
   * Every API call below takes the manifest id, so we MUST prefer
   * `plugin.pluginId` and only fall back to `plugin.id` when absent
   * (marketplace case). Pre-fix the fallback order was reversed, which
   * caused install/uninstall/configure to send the Mongo ObjectId as
   * the manifest id for already-installed plugins → backend looked up
   * pluginConfigs with `pluginId: <ObjectId-string>` and returned 404
   * "Plugin not installed" even though the row was right there.
   */
  const manifestId: string = plugin.pluginId || plugin.id;

  /**
   * Invalidate every plugin-related query key across the whole admin app.
   *
   * Different pages name the same data differently:
   *   - dashboard/Plugins.tsx       → ['installed-plugins', selectedProject]
   *   - dashboard/ProjectDetail.tsx → ['project-plugins', projectId]
   *   - project/Chatbot.tsx         → ['plugins', projectId, 'installed']
   *   - project/PluginDetail.tsx    → ['plugin-manifest', projectId, pluginId]
   *
   * Before this helper we only busted the first two — so after install /
   * uninstall, the chatbot page and the universal plugin-detail page
   * would still render the stale state ("still installed" / "still
   * removed") until a manual refresh. The single fix: use react-query's
   * predicate match against any key whose first segment starts with
   * "plugin". That catches all four variants + any future ones.
   */
  const invalidateAllPluginQueries = () => {
    // Match any query whose key contains "plugin" anywhere in any segment.
    // Catches every shape currently in use:
    //   ['installed-plugins', projectId]      (dashboard/Plugins.tsx)
    //   ['project-plugins', projectId]        (dashboard/ProjectDetail.tsx) ← the list parent
    //   ['plugins', projectId, 'installed']   (project/Chatbot.tsx)
    //   ['plugin-manifest', projectId, id]    (project/PluginDetail.tsx)
    //   ['plugin-health', projectId, id]      (project/PluginDetail.tsx)
    //
    // Earlier I used `first.startsWith('plugin')` which missed the
    // installed-plugins + project-plugins variants — those start with
    // 'installed' / 'project'. Result: after Remove, the marketplace and
    // project-detail lists kept showing the plugin until manual refresh.
    queryClient.invalidateQueries({
      predicate: (q) =>
        q.queryKey.some((seg) => typeof seg === 'string' && seg.toLowerCase().includes('plugin')),
    });
  };

  const installMutation = useMutation({
    mutationFn: () => apiHelpers.installPlugin(projectId, { pluginId: manifestId }),
    onSuccess: (response) => {
      // OAuth-style plugins return a redirectUrl instead of finishing install
      const data = (response as any)?.data?.data;
      if (data?.status === 'oauth_required' && data.redirectUrl) {
        window.location.href = data.redirectUrl;
        return;
      }
      toast.success(`${pluginName} installed`);
      invalidateAllPluginQueries();
      onActionComplete?.();
    },
    onError: (err: any) => {
      // Surface backend precondition errors (e.g. SEO Co-Pilot's "connect a
      // custom domain first" 412). Falls back to a generic message when the
      // backend doesn't supply one.
      const apiMessage =
        err?.response?.data?.error?.message ||
        err?.data?.error?.message ||
        err?.message ||
        'Failed to install plugin';
      toast.error(apiMessage, { duration: 8000 });
    },
  });

  const uninstallMutation = useMutation({
    mutationFn: () => apiHelpers.uninstallPlugin(projectId, manifestId),
    onSuccess: () => {
      // Without the toast + complete cache invalidation, the user clicked
      // "Remove" and saw nothing happen — looks like the button is broken.
      // Pre-fix this code only invalidated 2 of the 4 plugin query keys
      // used across the admin, leaving stale UI on Project Detail + the
      // universal Plugin Detail page.
      toast.success(`${pluginName} removed`);
      invalidateAllPluginQueries();
      onActionComplete?.();
    },
    onError: (err: any) => {
      const apiMessage =
        err?.response?.data?.error?.message ||
        err?.data?.error?.message ||
        err?.message ||
        'Failed to remove plugin';
      toast.error(apiMessage, { duration: 8000 });
    },
  });

  const handleConfigure = () => {
    const pluginId = manifestId;
    // Navigate to the universal plugin detail page
    navigate(`/projects/${projectId}/plugins/${pluginId}`);
  };

  const pluginName = plugin.name || plugin.pluginId;
  const pluginCategory = plugin.category || 'other';
  const pluginDescription = plugin.description || '';
  const pluginFeatures = plugin.features || [];

  return (
    <>
      <div className="bg-card overflow-hidden rounded-xl border">
        <div className="p-6">
          <div className="mb-4 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-lg">
                <Icon className="text-primary h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold">{pluginName}</h3>
                <span className="text-muted-foreground text-xs capitalize">{pluginCategory}</span>
              </div>
            </div>
            {isInstalled && (
              <span className="flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs text-green-600 dark:bg-green-900/30">
                <Check className="h-3 w-3" />
                Installed
              </span>
            )}
          </div>

          {pluginDescription && (
            <p className="text-muted-foreground mb-4 line-clamp-2 text-sm">{pluginDescription}</p>
          )}

          {pluginFeatures.length > 0 && (
            <ul className="text-muted-foreground mb-4 space-y-1 text-xs">
              {pluginFeatures.slice(0, 3).map((feature: string, i: number) => (
                <li key={i} className="flex items-center gap-2">
                  <Check className="h-3 w-3 text-green-500" />
                  {feature}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-muted/30 flex items-center gap-2 border-t px-6 py-3">
          {!projectId ? (
            <p className="text-muted-foreground text-sm">Select a project to install</p>
          ) : isInstalled ? (
            <>
              <button
                onClick={handleConfigure}
                className="text-primary hover:bg-primary/10 flex flex-1 items-center justify-center gap-1 rounded py-1.5 text-sm font-medium transition-colors"
              >
                <Settings className="h-4 w-4" />
                Configure
              </button>
              <button
                onClick={() => uninstallMutation.mutate()}
                disabled={uninstallMutation.isPending}
                className="rounded px-3 py-1.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                {uninstallMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Remove'
                )}
              </button>
            </>
          ) : showInstallButton ? (
            <button
              onClick={() => installMutation.mutate()}
              disabled={installMutation.isPending}
              className="bg-primary text-primary-foreground hover:bg-primary/90 flex flex-1 items-center justify-center rounded py-1.5 text-sm font-medium transition-colors"
            >
              {installMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Install'}
            </button>
          ) : null}
        </div>
      </div>

      {/* Configuration Modal for plugins without dedicated pages */}
      {showConfigModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background w-full max-w-md rounded-xl border p-6 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-lg">
                  <Icon className="text-primary h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold">{pluginName} Settings</h3>
                  <p className="text-muted-foreground text-xs capitalize">{pluginCategory}</p>
                </div>
              </div>
              <button
                onClick={() => setShowConfigModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="border-t pt-4">
              <div className="mb-4 rounded-lg bg-green-50 p-3 dark:bg-green-900/20">
                <p className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <Check className="h-4 w-4" />
                  Plugin is installed and active
                </p>
              </div>

              {pluginDescription && (
                <p className="text-muted-foreground mb-4 text-sm">{pluginDescription}</p>
              )}

              {pluginFeatures.length > 0 && (
                <div className="mb-4">
                  <p className="mb-2 text-sm font-medium">Features:</p>
                  <ul className="text-muted-foreground space-y-1 text-sm">
                    {pluginFeatures.map((feature: string, i: number) => (
                      <li key={i} className="flex items-center gap-2">
                        <Check className="h-3 w-3 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="text-muted-foreground rounded-lg bg-blue-50 p-3 text-sm dark:bg-blue-900/20">
                <p className="mb-1 font-medium text-blue-600 dark:text-blue-400">
                  No additional configuration needed
                </p>
                <p className="text-xs">
                  This plugin works automatically once installed. Integrate it using the SDK in your
                  frontend.
                </p>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowConfigModal(false)}
                className="bg-muted hover:bg-muted/80 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
